/**
 * Router + shell.
 *
 * Route map mirrors the signup flow in plan §23.2:
 *   /sign-in -> /plans -> (Stripe) -> /building -> /dashboard
 * plus the standing screens: connections, billing, data & portability, and the operator area.
 */
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loading, ErrorNote } from "./components/ui.js";
import { SessionProvider, homeRouteFor, useSession } from "./state/session.js";
import SignIn from "./routes/SignIn.js";
import Plans from "./routes/Plans.js";
import Building from "./routes/Building.js";
import Dashboard from "./routes/Dashboard.js";
import Connections from "./routes/Connections.js";
import Billing from "./routes/Billing.js";
import DataAndPortability from "./routes/DataAndPortability.js";
import Pair from "./routes/Pair.js";
import ConnectAgent from "./routes/ConnectAgent.js";
import LinkAgent from "./routes/Link.js";
import AdminDeployments from "./routes/admin/Deployments.js";
import AdminDeploymentDetail from "./routes/admin/DeploymentDetail.js";
import AdminCoupons from "./routes/admin/Coupons.js";
import AdminAudit from "./routes/admin/Audit.js";

function Shell({ children }: { children: React.ReactNode }) {
  const { me, signOut } = useSession();
  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand__mark" aria-hidden="true" />
          <span className="brand__name">The Construct</span>
        </div>
        {me ? (
          <nav className="nav" aria-label="Dashboard">
            <NavLink to="/dashboard" end>
              Overview
            </NavLink>
            <NavLink to="/connections">Connections</NavLink>
            <NavLink to="/billing">Billing</NavLink>
            <NavLink to="/data">Your data</NavLink>
            {me.account.isOperator ? <NavLink to="/admin">Operator</NavLink> : null}
          </nav>
        ) : null}
        <span className="topbar__spacer" />
        {me ? (
          <>
            <span className="topbar__who">{me.account.email}</span>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => void signOut()}>
              Sign out
            </button>
          </>
        ) : null}
      </header>
      <main className="main">
        <div className="wrap">{children}</div>
      </main>
      <footer className="footer">
        <span>Your words, in files you own.</span>
        <a href="https://theconstruct.site">theconstruct.site</a>
      </footer>
    </div>
  );
}

/** Requires a signed-in account; everything else is the sign-in screen's job. */
function Protected({ children }: { children: React.ReactNode }) {
  const { me, loading, error } = useSession();
  const location = useLocation();
  if (loading) return <Loading label="Checking your session" />;
  if (error) {
    return (
      <Shell>
        <ErrorNote error={error} onRetry={() => window.location.reload()} />
      </Shell>
    );
  }
  // The QUERY STRING travels with the path, not just the path. `/link?request=…` is a live
  // authorization request parked server-side; dropping the query on the way through sign-in would
  // land the customer on an empty consent screen with the agent still waiting.
  if (!me) {
    return <Navigate to="/sign-in" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  return <Shell>{children}</Shell>;
}

/** Operator-only area (plan §26). Invisible and unreachable for everyone else. */
function OperatorOnly({ children }: { children: React.ReactNode }) {
  const { me } = useSession();
  if (!me?.account.isOperator) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function Root() {
  const { me, loading } = useSession();
  if (loading) return <Loading label="Checking your session" />;
  return <Navigate to={homeRouteFor(me)} replace />;
}

export default function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route path="/" element={<Root />} />
        <Route path="/sign-in" element={<SignIn />} />

        <Route
          path="/plans"
          element={
            <Protected>
              <Plans />
            </Protected>
          }
        />
        <Route
          path="/building"
          element={
            <Protected>
              <Building />
            </Protected>
          }
        />
        <Route
          path="/dashboard"
          element={
            <Protected>
              <Dashboard />
            </Protected>
          }
        />
        <Route
          path="/connections"
          element={
            <Protected>
              <Connections />
            </Protected>
          }
        />
        <Route
          path="/billing"
          element={
            <Protected>
              <Billing />
            </Protected>
          }
        />
        <Route
          path="/data"
          element={
            <Protected>
              <DataAndPortability />
            </Protected>
          }
        />
        <Route
          path="/pair/:code?"
          element={
            <Protected>
              <Pair />
            </Protected>
          }
        />
        {/* How to point an agent here. The page a customer VISITS (PLAN-AGENT-LINKING §2.2). */}
        <Route
          path="/link-agent"
          element={
            <Protected>
              <ConnectAgent />
            </Protected>
          }
        />

        {/* Consent screen + device-code entry for agent linking (PLAN-AGENT-LINKING §2.2). The page
            an agent SENDS the customer to, which is why it lives at a separate path. */}
        <Route
          path="/link"
          element={
            <Protected>
              <LinkAgent />
            </Protected>
          }
        />

        <Route
          path="/admin"
          element={
            <Protected>
              <OperatorOnly>
                <AdminDeployments />
              </OperatorOnly>
            </Protected>
          }
        />
        <Route
          path="/admin/deployments/:id"
          element={
            <Protected>
              <OperatorOnly>
                <AdminDeploymentDetail />
              </OperatorOnly>
            </Protected>
          }
        />
        <Route
          path="/admin/coupons"
          element={
            <Protected>
              <OperatorOnly>
                <AdminCoupons />
              </OperatorOnly>
            </Protected>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <Protected>
              <OperatorOnly>
                <AdminAudit />
              </OperatorOnly>
            </Protected>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SessionProvider>
  );
}
